import { useEffect, useState } from 'react';
import { useOutbox, type ChecklistRun } from '@dia/db';
import { useT } from '@dia/i18n';
import { Button } from '@dia/ui';
import { supabase } from '../supabase';
import { RequireAuth, type Identity } from './lib/identity';
import { loadJobCatalog, loadRunItems, loadRuns, loadStaffDirectory, translateText, useLoad } from './lib/data';
import { needsReview } from './lib/checklist';
import { formatTime } from './lib/time';
import type { Lang } from './lib/text';
import { Card, Field, inputClass, Notice, Page } from './lib/ui';

/**
 * Review: approve a finished cleaning or send it back to be redone. Only a
 * manager or an owner can change review_status; the database enforces it
 * with a trigger, so this screen is a courtesy, not the lock.
 */
export function JefeRevisar() {
  return <RequireAuth need="manager">{(who) => <ReviewBody who={who} />}</RequireAuth>;
}

/** Writes a note in the reviewer's language and, when it can, in the other one too. */
async function bothLanguages(text: string, lang: Lang, token: string, audience: 'staff' | 'owner') {
  const other: Lang = lang === 'es' ? 'en' : 'es';
  if (!text) return { es: null, en: null };
  const r = await translateText(text, lang, other, token, audience, 5000);
  const shadow = r.ok ? r.text : null;
  return { es: lang === 'es' ? text : shadow, en: lang === 'en' ? text : shadow };
}

function ReviewBody({ who }: { who: Identity }) {
  const { t, lang } = useT();
  const { pending } = useOutbox();
  const runs = useLoad(loadRuns, `runs${pending}`, 60_000);
  const catalog = useLoad(loadJobCatalog, 'catalog');
  const directory = useLoad(loadStaffDirectory, 'directory');

  const waiting = (runs.data ?? []).filter((r) => needsReview(r));
  const nameOf = (id: string | null) => directory.data?.find((p) => p.id === id)?.name ?? '';

  return (
    <Page title={t('ops.review.title')} back="/jefe">
      <div className="flex flex-col gap-5">
        {waiting.length === 0 && !runs.loading && <Notice tone="info">{t('ops.review.none')}</Notice>}
        {waiting.map((run) => (
          <ReviewCard
            key={run.id}
            run={run}
            who={who}
            house={catalog.data?.houses.find((h) => h.id === run.house_id)}
            list={catalog.data?.checklists.find((c) => c.id === run.checklist_id)}
            person={nameOf(run.assigned_to)}
            lang={lang}
          />
        ))}
      </div>
    </Page>
  );
}

function ReviewCard({
  run,
  who,
  house,
  list,
  person,
  lang,
}: {
  run: ChecklistRun;
  who: Identity;
  house: { name_es: string; name_en: string } | undefined;
  list: { name_es: string; name_en: string } | undefined;
  person: string;
  lang: Lang;
}) {
  const { t } = useT();
  const { enqueue } = useOutbox();
  const items = useLoad(() => loadRunItems(run.id), `ritems:${run.id}`);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [urls, setUrls] = useState<string[]>([]);

  const rows = items.data ?? [];
  const photoPaths = [...new Set(rows.map((r) => r.photo_path).filter((p): p is string => Boolean(p)))];

  useEffect(() => {
    if (photoPaths.length === 0) return;
    let alive = true;
    void supabase.storage
      .from('run-photos')
      .createSignedUrls(photoPaths.slice(0, 24), 3600)
      .then(({ data }) => {
        if (alive && data) setUrls(data.map((d) => d.signedUrl).filter((u): u is string => Boolean(u)));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // The list of paths is the dependency, joined so it compares by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoPaths.join('|')]);

  const decide = async (verdict: 'approved' | 'redo') => {
    setBusy(true);
    try {
      const text = note.trim() || (verdict === 'redo' ? t('ops.review.redo_default_note') : '');
      const words = await bothLanguages(text, lang, who.token, who.role === 'owner' ? 'owner' : 'staff');
      await enqueue({
        table: 'checklist_runs',
        kind: 'update',
        payload: {
          review_status: verdict,
          reviewed_by: who.staffId,
          reviewed_at: new Date().toISOString(),
          review_note_es: words.es,
          review_note_en: words.en,
        },
        match: { client_id: run.client_id },
      });
      setSaved(true);
    } finally {
      setBusy(false);
    }
  };

  if (saved) return <Notice tone="ok">{t('ops.review.sent')}</Notice>;

  return (
    <Card>
      <h2 className="m-0 text-[1.15em] font-semibold">
        {house ? (lang === 'es' ? house.name_es : house.name_en) : t('ops.cleaning.house_wide')}
        {list ? `: ${lang === 'es' ? list.name_es : list.name_en}` : ''}
      </h2>
      {person && <p className="m-0">{t('ops.review.done_by', { name: person })}</p>}
      {run.completed_at && <p className="m-0 text-inkMuted">{t('ops.review.finished_at', { time: formatTime(run.completed_at, lang) })}</p>}
      <p className="m-0 mt-1">{t('ops.review.evidence', { done: rows.length, photos: photoPaths.length })}</p>

      {urls.length > 0 && (
        <ul className="m-0 mt-3 flex list-none flex-wrap gap-2 p-0">
          {urls.map((url) => (
            <li key={url}>
              <a href={url} target="_blank" rel="noopener noreferrer">
                <img src={url} alt="" loading="lazy" className="h-24 w-24 rounded border border-line object-cover" />
              </a>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex flex-col gap-3">
        <Field label={t('ops.review.note')}>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={inputClass} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Button size="lg" disabled={busy} onClick={() => void decide('approved')}>
            {t('ops.review.approve')}
          </Button>
          <Button size="lg" variant="danger" disabled={busy} onClick={() => void decide('redo')}>
            {t('ops.review.redo')}
          </Button>
        </div>
      </div>
    </Card>
  );
}
